import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "contexts/AuthContext";
import {
  Building2,
  ChevronsUpDown,
  CircleHelp,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useLayout } from "@/contexts/LayoutContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/button/Button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { useIsSystemAdmin } from "@/hooks/useIsSystemAdmin";
import { useIsTenantAdmin } from "@/hooks/useIsTenantAdmin";
import { FaqPath } from "@/shared/constants";

export function Navbar() {
  const { authUser, signOut, isLoggedIn } = useAuth();
  const { features } = useFeatureFlags();
  const { setNavbarHeightPx } = useLayout();
  const navbarRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { isSystemAdmin } = useIsSystemAdmin();
  const { isTenantAdmin } = useIsTenantAdmin();
  const showManageTenants = isSystemAdmin || isTenantAdmin;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const element = navbarRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const updateHeight = () => {
      setNavbarHeightPx(element.offsetHeight);
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
      setNavbarHeightPx(0);
    };
  }, [setNavbarHeightPx, features]);

  if (!isLoggedIn) return null;

  const handleSignOut = async () => {
    await signOut();
  };

  const openInNewTab = (path: string) => {
    setDropdownOpen(false);
    window.open(path, "_blank", "noopener,noreferrer");
  };

  return (
    <header ref={navbarRef} className="fixed z-50 w-full bg-background">
      <div className="flex items-center justify-between min-h-[3rem] py-[0.5rem] px-[1rem]">
        <Link
          to="/home"
          className="text-xl font-normal text-foreground"
          search={{
            redirect: window.location.pathname,
            downloadJobId: undefined,
          }}
        >
          {t("general:logoText")}
        </Link>

        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center space-x-2 px-[0.5rem] border-white shadow-none focus:outline-none"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={authUser?.photoURL ?? undefined}
                  alt={authUser?.displayName ?? ""}
                />
                <AvatarFallback>
                  {authUser?.email?.[0].toUpperCase() ?? "?"}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <span className="hidden text-sm font-medium text-sidebar-foreground sm:block">
                  {authUser?.displayName}
                </span>
                <span className="hidden text-xs font-light text-sidebar-foreground sm:block">
                  {authUser?.email}
                </span>
              </div>
              <ChevronsUpDown className="h-[1rem] w-[1rem] text-sidebar-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent sizeToTrigger align="start" className="">
            {showManageTenants && (
              <>
                <Button
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate({ to: "/admin/tenants" });
                  }}
                  variant="ghost"
                  icon={<Building2 className="w-[1rem] h-[1rem]" />}
                  className="w-full justify-start rounded-md font-normal py-[0.625rem] px-[0.5rem]"
                  label={t("admin:tenants.title")}
                />
                <DropdownMenuSeparator />
              </>
            )}
            <Button
              onClick={() => openInNewTab(FaqPath.WhyEssence)}
              variant="ghost"
              icon={<CircleHelp className="w-[1rem] h-[1rem]" />}
              className="w-full justify-start rounded-md font-normal py-[0.625rem] px-[0.5rem]"
              label={t("general:faq.whyEssence")}
            />
            <Button
              onClick={() => openInNewTab(FaqPath.DataUsage)}
              variant="ghost"
              icon={<ShieldCheck className="w-[1rem] h-[1rem]" />}
              className="w-full justify-start rounded-md font-normal py-[0.625rem] px-[0.5rem]"
              label={t("general:faq.dataUsage")}
            />
            <DropdownMenuSeparator />
            <Button
              onClick={handleSignOut}
              variant="ghost"
              icon={<LogOut className="w-[1rem] h-[1rem]" />}
              className="w-full justify-start rounded-md font-normal py-[0.625rem] px-[0.5rem]"
              label={t("general:buttons:logout")}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {features.infoBannerText && (
        <div className="py-[0.5rem] px-[0.25rem] bg-primary text-white text-center text-sm content-around whitespace-pre-line">
          <p>{features.infoBannerText}</p>
        </div>
      )}
    </header>
  );
}
